import React, { useEffect, useRef } from 'react';
import gsap from 'gsap';
import { ScrollTrigger } from 'gsap/ScrollTrigger';
import { ArrowRight, Check } from 'lucide-react';
import VideoPlaceholder from '../components/VideoPlaceholder';
import { STRIPE_9200 } from '../lib/links';

gsap.registerPlugin(ScrollTrigger);

const included = [
    'Brand-new MacBook Air (Apple Silicon) — yours to keep',
    'Full OpenClaw build, configured to your roadmap',
    'Up to 3 API integrations connected and verified',
    'Zoom pre-installed — 3-hour live training, on your machine',
    'Tony "The Wrench" Clawzini — your on-machine AI mechanic',
    'RoofClaw Community — private Telegram, 6 months free, then $99/mo',
    'Winning Wednesday — weekly group webinar, ongoing',
];

const BuildWelcomePage = () => {
    const rootRef = useRef(null);

    useEffect(() => {
        const ctx = gsap.context(() => {
            gsap.utils.toArray('.welcome-section').forEach((section) => {
                gsap.from(section.querySelectorAll('.welcome-element'), {
                    y: 40,
                    opacity: 0,
                    duration: 1,
                    stagger: 0.08,
                    ease: 'power3.out',
                    scrollTrigger: { trigger: section, start: 'top 80%' },
                });
            });
        }, rootRef);
        return () => ctx.revert();
    }, []);

    return (
        <div ref={rootRef} className="min-h-screen bg-[#0A0A14] text-[#F0EFF4] font-heading selection:bg-[#7B61FF] selection:text-[#F0EFF4] flex flex-col">
            {/* Video */}
            <VideoPlaceholder
                label="VITO_VIDEO_2"
                caption="Vito kicks off your build. Two minutes."
            />

            {/* Headline */}
            <section className="welcome-section py-20 md:py-28 px-6">
                <div className="max-w-3xl mx-auto text-center">
                    <h1 className="welcome-element text-[#F0EFF4] font-bold text-5xl md:text-7xl tracking-tight leading-[1.05] mb-6">
                        Welcome to the Family.
                    </h1>
                    <p className="welcome-element font-drama italic text-[#7B61FF] text-2xl md:text-3xl leading-snug">
                        You saw what most contractors don&apos;t. That took courage.
                    </p>
                </div>
            </section>

            {/* Reaffirmation */}
            <section className="welcome-section py-16 md:py-20 px-6 border-t border-[#7B61FF]/15">
                <div className="max-w-3xl mx-auto space-y-8 text-[#F0EFF4]/80 text-lg leading-relaxed">
                    <p className="welcome-element font-bold text-[#F0EFF4] text-xl md:text-2xl">
                        You just made one of the best decisions of your business career.
                    </p>
                    <p className="welcome-element">
                        Not because we say so. Because you mapped it out. On that call, you looked at your operation — your software, your team, your bottlenecks — and you saw exactly where an AI agent fits.
                    </p>
                    <p className="welcome-element">
                        That clarity? Most contractors never get there. They stay stuck in the &ldquo;I&apos;ll figure it out later&rdquo; loop until later becomes never.
                    </p>
                    <p className="welcome-element font-bold text-[#F0EFF4]">You didn&apos;t.</p>
                </div>
            </section>

            {/* Here's what happens now */}
            <section className="welcome-section py-16 md:py-20 px-6 bg-[#18181B] border-y border-[#7B61FF]/15">
                <div className="max-w-3xl mx-auto space-y-6 text-[#F0EFF4]/80 text-lg leading-relaxed">
                    <p className="welcome-element font-bold text-[#F0EFF4] text-xl md:text-2xl">
                        Here&apos;s what happens now.
                    </p>
                    <p className="welcome-element">
                        We build your machine. Everything we mapped on your call — your integrations, your agent&apos;s identity, your communication channels — gets configured, tested, and verified before it leaves our hands.
                    </p>
                    <p className="welcome-element">
                        When it arrives at your door, your AI employee is already trained. Already connected. Already working.
                    </p>
                    <p className="welcome-element">
                        You plug it in. You power it on. You authenticate with the credentials on your quick start card.
                    </p>
                    <p className="welcome-element">
                        And then you get on a 3-hour Zoom call with us — screen-shared on your machine — and we walk you through everything together.
                    </p>
                </div>
            </section>

            {/* What you're getting */}
            <section className="welcome-section py-20 md:py-24 px-6">
                <div className="max-w-3xl mx-auto">
                    <p className="welcome-element font-data text-[#7B61FF] text-xs tracking-widest uppercase mb-6">
                        What You&apos;re Getting
                    </p>
                    <ul className="flex flex-col gap-4 mb-10">
                        {included.map((item, i) => (
                            <li key={i} className="welcome-element flex items-start gap-3">
                                <Check size={18} className="text-[#7B61FF] shrink-0 mt-[3px]" />
                                <span className="text-[#F0EFF4]/85 text-base md:text-lg leading-relaxed">{item}</span>
                            </li>
                        ))}
                    </ul>

                    <div className="welcome-element rounded-[2rem] border border-[#7B61FF]/30 bg-[#18181B] p-8 md:p-10 text-center">
                        <p className="font-data text-[#F0EFF4]/40 text-xs tracking-widest uppercase mb-4">
                            Your investment today
                        </p>
                        <div className="inline-flex items-baseline gap-4 md:gap-6">
                            <span className="text-[#F0EFF4]/40 text-2xl md:text-3xl font-bold line-through leading-none">
                                $10,000
                            </span>
                            <span className="text-[#F0EFF4]/40 text-2xl">→</span>
                            <span className="font-drama text-[#F0EFF4] text-6xl md:text-7xl leading-none">
                                $9,200
                            </span>
                        </div>
                        <p className="font-data text-[#F0EFF4]/50 text-xs tracking-wide mt-4">
                            (Your $800 AI Readiness Session applied.)
                        </p>
                    </div>
                </div>
            </section>

            {/* CTA */}
            <section className="welcome-section py-16 px-6">
                <div className="max-w-3xl mx-auto text-center">
                    <a
                        href={STRIPE_9200}
                        target="_blank"
                        rel="noopener noreferrer"
                        className="welcome-element btn-magnetic inline-flex items-center justify-center gap-3 bg-[#7B61FF] text-[#F0EFF4] px-10 py-5 rounded-full font-bold text-lg tracking-wide shadow-[0_0_30px_rgba(123,97,255,0.4)] hover:shadow-[0_0_50px_rgba(123,97,255,0.7)] transition-shadow w-full sm:w-auto"
                    >
                        Complete My Build — $9,200 <ArrowRight size={20} />
                    </a>
                    <p className="welcome-element text-[#F0EFF4]/50 text-xs mt-6 font-data tracking-wide max-w-lg mx-auto">
                        Questions before you complete? Reply to the email we sent you or message Vito directly on Telegram.
                    </p>
                </div>
            </section>

            {/* What Happens Next — timeline */}
            <section className="welcome-section py-20 md:py-24 px-6 bg-[#18181B] border-t border-[#7B61FF]/15">
                <div className="max-w-5xl mx-auto">
                    <p className="welcome-element font-data text-[#7B61FF] text-xs tracking-widest uppercase mb-10 text-center">
                        What Happens After You Pay
                    </p>
                    <div className="grid grid-cols-1 md:grid-cols-3 gap-6">
                        {[
                            {
                                step: 'Step 1',
                                when: 'Within 24 hours',
                                body: "You'll receive a confirmation email from Vito with your build start date and any final pre-reqs we need from you.",
                            },
                            {
                                step: 'Step 2',
                                when: 'Build Phase (5\u201310 business days)',
                                body: "We configure your machine. Connect your APIs. Write your agent's identity. Test everything. You'll hear from us if we need anything.",
                            },
                            {
                                step: 'Step 3',
                                when: 'Ship & Train',
                                body: 'Your machine ships. You receive tracking. We schedule your 3-hour Zoom training session for the day it arrives. You go live.',
                            },
                        ].map((s, i) => (
                            <div
                                key={i}
                                className="welcome-element rounded-[1.5rem] border border-[#F0EFF4]/10 bg-[#0A0A14] p-6"
                            >
                                <p className="font-data text-[#7B61FF] text-xs tracking-widest uppercase mb-1">
                                    {s.step}
                                </p>
                                <p className="text-[#F0EFF4] font-bold text-lg mb-3">{s.when}</p>
                                <p className="text-[#F0EFF4]/70 text-sm leading-relaxed">{s.body}</p>
                            </div>
                        ))}
                    </div>
                </div>
            </section>

            {/* Minimal Footer (no site nav) */}
            <footer className="mt-auto py-12 px-6 text-center border-t border-[#F0EFF4]/10">
                <p className="font-data text-[#F0EFF4]/40 text-xs tracking-wide mb-2">
                    RoofClaw is a product of RBP Consulting, Inc.
                </p>
                <p className="font-data text-[#F0EFF4]/40 text-xs tracking-wide">
                    Questions?{' '}
                    <a href="mailto:vito@roofclaw.com" className="text-[#F0EFF4]/60 hover:text-[#7B61FF] transition-colors">
                        vito@roofclaw.com
                    </a>
                </p>
            </footer>
        </div>
    );
};

export default BuildWelcomePage;
